import type { Cart, CartItem, Product, ProductVariant } from '../types/index.js';
import { storeApi } from '../api/store-api.js';
import { mockCoupons } from '../mock/index.js';

type CartListener = (cart: Cart) => void;

/**
 * Global cart state manager using localStorage for persistence.
 * Implements a simple pub/sub pattern for reactive updates.
 */
class CartStateManager {
  private static instance: CartStateManager;
  private cart: Cart;
  private listeners: Set<CartListener> = new Set();
  private storageKey = 'landi-store-cart';
  /** Discount amount from last successful API coupon validation */
  private apiValidatedDiscount = 0;

  private constructor() {
    this.cart = this.loadFromStorage();
  }

  static getInstance(): CartStateManager {
    if (!CartStateManager.instance) {
      CartStateManager.instance = new CartStateManager();
    }
    return CartStateManager.instance;
  }

  private loadFromStorage(): Cart {
    if (typeof window === 'undefined') {
      return this.emptyCart();
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return this.recalculateTotals(parsed.items || []);
      }
    } catch (e) {
      console.error('Failed to load cart from storage:', e);
    }
    return this.emptyCart();
  }

  private saveToStorage(): void {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        items: this.cart.items,
        couponCode: this.cart.couponCode,
      }));
    } catch (e) {
      console.error('Failed to save cart to storage:', e);
    }
  }

  private emptyCart(): Cart {
    return {
      items: [],
      subtotal: 0,
      tax: 0,
      shipping: 0,
      discount: 0,
      total: 0,
      itemCount: 0,
    };
  }

  private recalculateTotals(
    items: CartItem[],
    couponCode?: string,
    discountOverride?: number
  ): Cart {
    const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    
    // Simplified tax calculation (8.25% default)
    const taxRate = 0.0825;
    const tax = subtotal * taxRate;
    
    // Shipping calculation (free over $200, else $9.99)
    const shipping = subtotal >= 200 ? 0 : 9.99;
    
    let discount = discountOverride ?? 0;
    if (
      discountOverride === undefined &&
      couponCode &&
      storeApi.isApiMode() &&
      this.apiValidatedDiscount > 0
    ) {
      discount = this.apiValidatedDiscount;
    } else if (discountOverride === undefined && couponCode && !storeApi.isApiMode()) {
      const coupon = mockCoupons.find(
        (c) => c.code.toUpperCase() === couponCode.toUpperCase() && c.active
      );
      if (coupon) {
        if (!coupon.minOrderValue || subtotal >= coupon.minOrderValue) {
          if (coupon.discountType === 'percentage') {
            discount = subtotal * (coupon.discountValue / 100);
          } else if (coupon.discountType === 'fixed') {
            discount = coupon.discountValue;
          } else if (coupon.discountType === 'free_shipping') {
            discount = shipping;
          }
        }
      }
    }
    
    const total = subtotal + tax + shipping - discount;

    return {
      items,
      subtotal: Math.round(subtotal * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      shipping: Math.round(shipping * 100) / 100,
      discount: Math.round(discount * 100) / 100,
      total: Math.round(total * 100) / 100,
      couponCode,
      itemCount,
    };
  }

  private notify(): void {
    this.listeners.forEach(listener => listener(this.cart));
  }

  getCart(): Cart {
    return { ...this.cart };
  }

  subscribe(listener: CartListener): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.cart);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  addItem(product: Product, variant?: ProductVariant, quantity: number = 1): void {
    const items = [...this.cart.items];
    const itemId = variant ? `${product.id}-${variant.id}` : product.id;
    
    const existingIndex = items.findIndex(item => item.id === itemId);
    
    if (existingIndex >= 0) {
      // Update existing item
      items[existingIndex] = {
        ...items[existingIndex],
        quantity: items[existingIndex].quantity + quantity,
      };
    } else {
      // Add new item
      const newItem: CartItem = {
        id: itemId,
        productId: product.id,
        variantId: variant?.id,
        name: product.name,
        variantName: variant?.name,
        sku: variant?.sku || product.sku,
        price: variant?.price ?? product.price,
        quantity,
        image: product.images[0]?.url,
        maxQuantity: variant?.inventoryQuantity ?? product.inventoryQuantity,
      };
      items.push(newItem);
    }

    this.cart = this.recalculateTotals(items, this.cart.couponCode);
    this.saveToStorage();
    this.notify();
  }

  updateQuantity(itemId: string, quantity: number): void {
    if (quantity < 1) {
      this.removeItem(itemId);
      return;
    }

    const items = this.cart.items.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    );

    this.cart = this.recalculateTotals(items, this.cart.couponCode);
    this.saveToStorage();
    this.notify();
  }

  removeItem(itemId: string): void {
    const items = this.cart.items.filter(item => item.id !== itemId);
    this.cart = this.recalculateTotals(items, this.cart.couponCode);
    this.saveToStorage();
    this.notify();
  }

  async applyCoupon(code: string): Promise<{ success: boolean; message?: string }> {
    const subtotal = this.cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    let discount = 0;

    if (storeApi.isApiMode()) {
      const result = await storeApi.validateCoupon(code, subtotal);
      if (!result.valid) {
        return { success: false, message: result.message ?? 'Invalid coupon' };
      }
      discount = result.discount;
      this.apiValidatedDiscount = result.discount;
    } else {
      const coupon = mockCoupons.find(
        (c) => c.code.toUpperCase() === code.toUpperCase() && c.active
      );
      if (!coupon) {
        return { success: false, message: 'Invalid coupon' };
      }
      if (coupon.minOrderValue && subtotal < coupon.minOrderValue) {
        return { success: false, message: `Minimum order $${coupon.minOrderValue}` };
      }
      if (coupon.discountType === 'percentage') {
        discount = subtotal * (coupon.discountValue / 100);
      } else if (coupon.discountType === 'fixed') {
        discount = coupon.discountValue;
      } else if (coupon.discountType === 'free_shipping') {
        discount = this.cart.shipping;
      }
    }

    this.cart = this.recalculateTotals(this.cart.items, code, discount);
    this.saveToStorage();
    this.notify();
    return { success: true };
  }

  removeCoupon(): void {
    this.apiValidatedDiscount = 0;
    this.cart = this.recalculateTotals(this.cart.items, undefined);
    this.saveToStorage();
    this.notify();
  }

  clearCart(): void {
    this.cart = this.emptyCart();
    this.saveToStorage();
    this.notify();
  }
}

// Export singleton instance
export const cartState = CartStateManager.getInstance();

// Export type for external use
export type { CartStateManager };
